import React, { useState, useMemo } from "react";
import {
    Box, Typography, Card, CardContent, Divider, List, Tabs, Tab, useMediaQuery, useTheme
} from "@mui/material";
import FarmTypeCountChart from './FarmTypeCountChart';
import AvgAreaPerFarmTypeChart from './AvgAreaPerFarmTypeChart';
import { useNavigate } from "react-router-dom"; // Import useNavigate
import { useFarmSelection } from '../context/FarmSelectionContext';

// Helper function to get farm size display
const getFarmSizeDisplay = (farm) => {
    // Debug logging
    console.log('getFarmSizeDisplay called for farm:', farm.name);
    console.log('  farm.farm_size:', farm.farm_size, 'type:', typeof farm.farm_size);
    console.log('  farm.farm_size_unit:', farm.farm_size_unit, 'type:', typeof farm.farm_size_unit);
    console.log('  farm.area:', farm.area, 'type:', typeof farm.area);
    console.log('  farm.area_unit:', farm.area_unit, 'type:', typeof farm.area_unit);
    
    // Check if farm_size and farm_size_unit exist and are not null/empty
    if (farm.farm_size && farm.farm_size_unit && farm.farm_size !== 0) {
        const result = `${farm.farm_size} ${farm.farm_size_unit}`;
        console.log('  Using farm_size:', result);
        return result;
    }
    // Fallback to calculated area
    if (farm.area && farm.area_unit) {
        const result = `${farm.area} ${farm.area_unit}`;
        console.log('  Using area:', result);
        return result;
    }
    // If no size data available
    console.log('  No size data available');
    return "Size not available";
};

// Helper function to get farm size value for calculations (returns value in hectares)
const getFarmSizeValue = (farm) => {
    // Check if farm_size exists and is not null/empty (already in hectares)
    if (farm.farm_size && farm.farm_size !== 0) {
        return farm.farm_size; // Already in hectares, no conversion needed
    }
    // Fallback to calculated area (in square meters, needs conversion to hectares)
    if (farm.area) {
        return farm.area / 10000; // Convert from square meters to hectares
    }
    // If no size data available
    return 0;
};

const FarmsInfoPanel = ({
    farmLocations,
    selectedCountries,
    selectedStates,
    selectedCities,
    selectedFarmTypes,
    searchQuery,
    userData,
    idToken,
    isMobile
}) => {
    const [filteredFarms, setFilteredFarms] = useState(farmLocations);
    const [selectedTab, setSelectedTab] = useState(0);
    const navigate = useNavigate(); // Initialize navigate
    const { setCurrentFarm } = useFarmSelection();
    const theme = useTheme();
    const isMobileView = useMediaQuery(theme.breakpoints.down('md')) || isMobile;

    // Handle tab change
    const handleTabChange = (event, newValue) => {
        setSelectedTab(newValue);
    };

    // Handle card click
    const handleCardClick = (farmId, farmZones, userData) => {
        const activeZones = farmZones.filter(zone => zone.deleted !== false);
        const selectedFarm = farmLocations.find(f => f.id === farmId);
        setCurrentFarm(selectedFarm);
        navigate(`/farm/${farmId}`, { state: { idToken, zones: activeZones, farmLocations, userData } });
    };

    // Filter farms based on user selections
    useMemo(() => {
        const filtered = farmLocations.filter((farm) => {
            const matchesCountry = selectedCountries.length === 0 || selectedCountries.includes(farm.country);
            const matchesState = selectedStates.length === 0 || selectedStates.includes(farm.state);
            const matchesCity = selectedCities.length === 0 || selectedCities.includes(farm.city);

            const matchesType = selectedFarmTypes.length === 0 || selectedFarmTypes.includes(farm.type);
            const searchQueryLower = searchQuery.toLowerCase();
            const matchesSearch =
                searchQueryLower === '' ||
                farm.name.toLowerCase().includes(searchQueryLower) ||
                farm.country.toLowerCase().includes(searchQueryLower) ||
                farm.state.toLowerCase().includes(searchQueryLower) ||
                farm.city.toLowerCase().includes(searchQueryLower) ||
                farm.type.toLowerCase().includes(searchQueryLower);

            return matchesCountry && matchesState && matchesCity && matchesType && matchesSearch;
        });

        // Sort farms alphabetically by name
        const sortedFarms = filtered.sort((a, b) => a.name.localeCompare(b.name));
        setFilteredFarms(sortedFarms);
    }, [farmLocations, selectedCountries, selectedStates, selectedCities, selectedFarmTypes, searchQuery]);

    // Count farm types and calculate average area per farm type
    const farmTypeData = useMemo(() => {
        const typeData = {};

        filteredFarms.forEach(farm => {
            if (farm.type) {
                if (!typeData[farm.type]) {
                    typeData[farm.type] = {
                        count: 0,
                        totalArea: 0,
                    };
                }
                typeData[farm.type].count += 1;
                typeData[farm.type].totalArea += getFarmSizeValue(farm) || 0;
            }
        });

        return Object.keys(typeData).map(type => ({
            type,
            count: typeData[type].count,
            avgArea: typeData[type].totalArea / typeData[type].count // Already in hectares
        }));
    }, [filteredFarms]);

    // Mobile Layout - Full width panel
    if (isMobileView) {
        return (
            <Box sx={{ 
                width: '100%', 
                height: '100%', 
                backgroundColor: '#181F2A',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
            }}>
                {/* Tabs */}
                <Box sx={{ px: 2, pt: 1 }}>
                    <Tabs
                        value={selectedTab}
                        onChange={handleTabChange}
                        textColor="inherit"
                        indicatorColor="primary"
                        centered
                        sx={{
                            '& .MuiTabs-flexContainer': {
                                borderBottom: '1px solid rgba(255,255,255,0.1)',
                            },
                            '& .MuiTab-root': {
                                color: '#AAB4C8',
                                minWidth: '50%',
                                flex: 1,
                                fontSize: 14,
                                fontWeight: 500,
                            },
                            '& .Mui-selected': {
                                color: '#F9AA33',
                            },
                            '& .MuiTabs-indicator': {
                                backgroundColor: '#F9AA33',
                            }
                        }}
                    >
                        <Tab label="Farms List" />
                        <Tab label="Statistics" />
                    </Tabs>
                </Box>

                {/* Content - Scrollable */}
                <Box sx={{ flexGrow: 1, overflowY: 'auto', padding: '8px', pt: 2, pb: 8 }}>
                    {selectedTab === 0 && (
                        <List sx={{ width: '100%', padding: 0, backgroundColor: 'transparent' }}>
                            {filteredFarms.map((farm) => (
                                <Card
                                    key={farm.id}
                                    sx={{ 
                                        marginBottom: '12px', 
                                        backgroundColor: 'transparent', 
                                        color: 'white', 
                                        cursor: 'pointer',
                                        borderRadius: 2,
                                        border: '1px solid rgba(255,255,255,0.1)',
                                        boxShadow: 'none',
                                        '&:hover': {
                                            backgroundColor: 'rgba(255,255,255,0.05)',
                                            border: '1px solid rgba(255,255,255,0.2)',
                                        }
                                    }}
                                    onClick={() => handleCardClick(farm.id, farm.zones, userData)}
                                >
                                    <CardContent sx={{ py: 2, px: 3 }}>
                                        <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, fontSize: 16, mb: 1 }}>
                                            {farm.name}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#AAB4C8', fontSize: 13, mb: 0.5 }}>
                                            {farm.country}, {farm.state}, {farm.city}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#AAB4C8', fontSize: 13, mb: 0.5 }}>
                                            Type: {farm.type}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#AAB4C8', fontSize: 13, mb: 0.5 }}>
                                            Size: {getFarmSizeDisplay(farm)}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#F9AA33', fontSize: 13, fontWeight: 500 }}>
                                            Sections: {farm.zones.length}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            ))}
                        </List>
                    )}

                    {selectedTab === 1 && (
                        <Box sx={{ padding: 0 }}>
                            <FarmTypeCountChart farmTypeData={farmTypeData} />
                            <Divider sx={{ backgroundColor: 'rgba(255,255,255,0.1)', margin: '16px 0' }} />
                            <AvgAreaPerFarmTypeChart farmTypeData={farmTypeData} />
                        </Box>
                    )}
                </Box>
            </Box>
        );
    }

    // Desktop Layout - Same as mobile but with different height
    return (
        <Box sx={{ 
            width: '100%', 
            height: '100%', 
            backgroundColor: '#181F2A',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
        }}>
            {/* Tabs */}
            <Box sx={{ px: 2, pt: 1 }}>
                <Tabs
                    value={selectedTab}
                    onChange={handleTabChange}
                    textColor="inherit"
                    indicatorColor="primary"
                    centered
                    sx={{
                        '& .MuiTabs-flexContainer': {
                            borderBottom: '1px solid rgba(255,255,255,0.1)',
                        },
                        '& .MuiTab-root': {
                            color: '#AAB4C8',
                            minWidth: '50%',
                            flex: 1,
                            fontSize: 14,
                            fontWeight: 500,
                        },
                        '& .Mui-selected': {
                            color: '#F9AA33',
                        },
                        '& .MuiTabs-indicator': {
                            backgroundColor: '#F9AA33',
                        }
                    }}
                >
                    <Tab label="Farms List" />
                    <Tab label="Statistics" />
                </Tabs>
            </Box>

            {/* Content - Scrollable */}
            <Box sx={{ flexGrow: 1, overflowY: 'auto', padding: '8px', pt: 2, pb: 8 }}>
                {selectedTab === 0 && (
                    <List sx={{ width: '100%', padding: 0, backgroundColor: 'transparent' }}>
                        {filteredFarms.map((farm) => (
                            <Card
                                key={farm.id}
                                sx={{ 
                                    marginBottom: '12px', 
                                    backgroundColor: 'transparent', 
                                    color: 'white', 
                                    cursor: 'pointer',
                                    borderRadius: 2,
                                    border: '1px solid rgba(255,255,255,0.1)',
                                    boxShadow: 'none',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255,255,255,0.05)',
                                        border: '1px solid rgba(255,255,255,0.2)',
                                    }
                                }}
                                onClick={() => handleCardClick(farm.id, farm.zones, userData)}
                            >
                                <CardContent sx={{ py: 2, px: 3 }}>
                                    <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, fontSize: 16, mb: 1 }}>
                                        {farm.name}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#AAB4C8', fontSize: 13, mb: 0.5 }}>
                                        {farm.country}, {farm.state}, {farm.city}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#AAB4C8', fontSize: 13, mb: 0.5 }}>
                                        Type: {farm.type}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#AAB4C8', fontSize: 13, mb: 0.5 }}>
                                        Size: {getFarmSizeDisplay(farm)}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#F9AA33', fontSize: 13, fontWeight: 500 }}>
                                        Sections: {farm.zones.length}
                                    </Typography>
                                </CardContent>
                            </Card>
                        ))}
                    </List>
                )}

                {selectedTab === 1 && (
                    <Box sx={{ padding: 0 }}>
                        <FarmTypeCountChart farmTypeData={farmTypeData} />
                        <Divider sx={{ backgroundColor: 'rgba(255,255,255,0.1)', margin: '16px 0' }} />
                        <AvgAreaPerFarmTypeChart farmTypeData={farmTypeData} />
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default FarmsInfoPanel;
